import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {noWhitespaceValidator, required} from '../../../../../../../shared/utils';

export interface WaConversionsEvent {
    eventName: string;
    customData: { [key: string]: any };
}

export interface WaConversions {
    whatsappBusinessId: string;
    datasetId: string;
    events: WaConversionsEvent[];
}

export class WaSettings {

    static toForm(metadata: { [index: string]: any }, canEdit: boolean): FormArray {
        return this.buildForm(this.getConversion(metadata), canEdit);
    }

    private static getConversion(metadata: { [index: string]: any }): WaConversions[] {
        const waConversions: WaConversions[] = [];

        const conversion = metadata['conversion'] ? metadata['conversion'] : {};

        if (Object.keys(conversion).length === 0) {
            return [
                {
                    whatsappBusinessId: '',
                    datasetId: '',
                    events: [{
                        eventName: '',
                        customData: {}
                    }]
                }
            ];
        }

        for (const conversionKey in conversion) {
            const conversionData: WaConversions = {
                whatsappBusinessId: conversionKey,
                datasetId: conversion[conversionKey]?.datasetId || '',
                events: conversion[conversionKey]?.events || [],
            };

            waConversions.push(conversionData);
        }

        return waConversions;
    }

    private static buildForm(formData: WaConversions[], canEdit: boolean): FormArray {
        const conversionsForm: FormArray = new FormArray([]);

        for (const conversion of formData) {
            conversionsForm.push(this.buildConversionForm(conversion, canEdit));
        }

        return conversionsForm;
    }

    private static buildConversionForm(conversion: WaConversions, canEdit: boolean): FormGroup {
        return new FormGroup({
            whatsappBusinessId: new FormControl({
                value: conversion.whatsappBusinessId,
                disabled: !canEdit
            }, required('Whatsapp Business Id')),
            datasetId: new FormControl({
                value: conversion.datasetId,
                disabled: !canEdit
            }, required('Dataset Id')),
            events: this.buildEventForm(conversion.events, canEdit)
        });
    }

    private static buildEventForm(events: WaConversionsEvent[], canEdit: boolean): FormArray {
        const eventsForm: FormArray = new FormArray([]);

        for (const event of events) {
            const form = this.eventFormTemplate(event, canEdit);

            eventsForm.push(form);
        }

        return eventsForm;
    }

    private static buildCustomDataForm(customData: { [key: string]: any }, canEdit: boolean): FormArray {
        const customDataFormArray: FormArray = new FormArray([]);
        for (const key in customData) {
            if (customData.hasOwnProperty(key)) {
                customDataFormArray.push(this.customDataFormTemplate(key, customData[key], canEdit));
            }
        }
        return customDataFormArray;
    }

    public static eventFormTemplate(event: WaConversionsEvent, canEdit: boolean) {
        return new FormGroup({
            eventName: new FormControl({
                value: event.eventName || '',
                disabled: !canEdit
            }, required('Event Name')),
            customData: this.buildCustomDataForm(event.customData, canEdit)
        });
    }

    public static customDataFormTemplate(key: string, value: string, canEdit: boolean): FormGroup {
        return new FormGroup({
            key: new FormControl({
                value: key || '',
                disabled: !canEdit
            }, noWhitespaceValidator()),
            value: new FormControl({
                value: value || '',
                disabled: !canEdit
            }, noWhitespaceValidator())
        });
    }

    public static setConversion(rawData: any): { [key: string]: any } {

        const customData = (cData: [{ key: string, value: string }]): {} => {
            if (cData && cData.length > 0) {
                const customDataObj: { [key: string]: any } = {};
                for (const data of cData) {
                    if (!data.key || !data.value) {
                        continue; // Skip if key or value is missing
                    }
                    customDataObj[data.key] = data.value;
                }
                return customDataObj;
            }
            return {};
        };

        const conversions: { [key: string]: any } = {};

        for (const data of rawData) {
            conversions[data.whatsappBusinessId] = {
                datasetId: data.datasetId || '',
                events: data.events.map((event: any) => {
                    return {
                        eventName: event.eventName,
                        customData: customData(event.customData)
                    };
                })
            };
        }


        return conversions;
    }
}
